import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useScrollReveal, useScrollProgress } from '@/hooks/useScrollReveal';
import { useCountUp } from '@/hooks/useCountUp';
import './TeamLeaderReport.css';

interface MemberData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  initials: string;
  role: string;
  overall_score: number;
  overall_tier: string;
  leadership_family: string;
  identity_type: string;
  family_color: string;
  eq_pillars: {
    self_awareness: number;
    self_regulation: number;
    motivation: number;
    empathy: number;
    social_skill: number;
  };
  culture_impact_score: number;
  identity: {
    strengths: string[];
    blind_spots: string[];
    stress_behaviors: string[];
    best_utilized: string;
  };
  attention_flag: {
    type: string;
    color: string;
    label: string;
    text: string;
  };
}

interface StrengthCard {
  title: string;
  pillar: string;
  icon: string;
  score_display: string;
  avg: number;
  description: string;
  top_contributors: { name: string; score: number }[];
  color: string;
}

interface GapCard {
  number: number;
  priority: string;
  priority_label: string;
  title: string;
  description: string;
  most_affected_text: string;
  bar_avg: number;
  bar_benchmark: number;
  bar_label: string;
  bar_display: string;
}

interface GapSidebar {
  biggest_risk: { title: string; score: string; description: string };
  most_urgent_person: { title: string; score: string; description: string };
  fastest_win: { title: string; score: string; description: string };
}

interface ChemistryPair {
  member_a: { id: string; name: string; initials: string; family_color: string };
  member_b: { id: string; name: string; initials: string; family_color: string };
  tier: 'amplifier' | 'tensioner' | 'watchlist';
  chemistry_score: number;
  description: string;
  pair_label: string;
  leader_action: string;
}

interface PressureData {
  per_person: {
    member_id: string;
    name: string;
    initials: string;
    family_color: string;
    description: string;
    core_phrase: string;
  }[];
  collective_pattern: string;
  leader_protocol: string;
}

interface ActionCard {
  number: number;
  urgency: string;
  urgency_color: string;
  title: string;
  description: string;
  who: { name: string; initials: string; color: string }[];
  tied_to_label: string;
  tied_to_chip_color: string;
}

interface LeaderReportData {
  organization: {
    name: string;
    report_date: string;
    retake_date: string;
    leader: { name: string; role: string; member_id: string | null };
  };
  team_summary: {
    member_count: number;
    health_score: number;
    health_tier: string;
    awareness_avg: number;
    drive_avg: number;
    culture_impact_avg: number;
    watchlist_pair_count: number;
    critical_gap_pillar: string;
    top_strength_pillar: string;
    family_distribution: Record<string, number>;
  };
  members: MemberData[];
  team_pillars: Record<string, { avg: number; tier: string }>;
  strengths: StrengthCard[];
  gaps: GapCard[];
  gap_sidebar: GapSidebar;
  chemistry: ChemistryPair[];
  pressure: PressureData;
  actions: ActionCard[];
  generated_narratives: {
    picture_intro_p1: string;
    picture_intro_p2: string;
    strengths_so_what: string;
    collective_pressure_pattern: string;
    pressure_leader_protocol: string;
  };
  benchmarks: Record<string, number>;
}

function tierColor(score: number): string {
  if (score >= 85) return '#C9A84C';
  if (score >= 70) return '#35C785';
  if (score >= 50) return '#F0A030';
  return '#E05858';
}

function barColor(score: number): string {
  if (score >= 75) return 'var(--tlr-green)';
  if (score >= 55) return 'var(--tlr-amber)';
  return 'var(--tlr-red)';
}

function flagBg(color: string): string {
  switch (color) {
    case 'red': return 'var(--tlr-red-bg)';
    case 'green': return 'var(--tlr-green-bg)';
    case 'amber': return 'var(--tlr-amber-bg)';
    case 'gold': return 'var(--tlr-gold-bg)';
    default: return 'rgba(255,255,255,0.03)';
  }
}

function flagBorder(color: string): string {
  switch (color) {
    case 'red': return '1px solid rgba(224,88,88,0.2)';
    case 'green': return '1px solid rgba(53,199,133,0.2)';
    case 'amber': return '1px solid rgba(240,160,48,0.2)';
    case 'gold': return '1px solid var(--tlr-gold-dim)';
    default: return '1px solid var(--tlr-border)';
  }
}

function flagDotColor(color: string): string {
  switch (color) {
    case 'red': return 'var(--tlr-red)';
    case 'green': return 'var(--tlr-green)';
    case 'amber': return 'var(--tlr-amber)';
    case 'gold': return 'var(--tlr-gold)';
    default: return 'var(--tlr-text3)';
  }
}

function flagLabelColor(color: string): string {
  switch (color) {
    case 'red': return '#FF8888';
    case 'green': return '#6DDEA8';
    case 'amber': return '#FFBC58';
    case 'gold': return 'var(--tlr-gold2)';
    default: return 'var(--tlr-text)';
  }
}

function chipClass(color: string): string {
  const map: Record<string, string> = {
    red: 'tlr-ch-red', green: 'tlr-ch-green', amber: 'tlr-ch-amber',
    gold: 'tlr-ch-gold', blue: 'tlr-ch-blue',
  };
  return map[color] || 'tlr-ch-muted';
}

function gapPriorityColor(priority: string): string {
  if (priority === 'critical') return 'var(--tlr-red)';
  if (priority === 'significant') return 'var(--tlr-amber)';
  return 'var(--tlr-amber)';
}

function gapBorderColor(priority: string): string {
  if (priority === 'critical') return 'rgba(224,88,88,0.25)';
  return 'rgba(240,160,48,0.2)';
}

function HealthCounter({ target, visible }: { target: number; visible: boolean }) {
  const value = useCountUp(target, 1600, visible);
  return <span className="tlr-health-grade">{value}</span>;
}

function renderBoldText(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
}

export default function TeamLeaderReport() {
  const { orgId } = useParams<{ orgId: string }>();
  const [data, setData] = useState<LeaderReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroVisible, setHeroVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const isPrintMode = new URLSearchParams(window.location.search).get('print') === '1';
  const barsTriggered = useRef<Set<string>>(new Set());

  const observe = useScrollReveal(0.07);
  useScrollProgress('tlr-progress-bar');

  useEffect(() => {
    fetch(`/api/team/${orgId}/leader-report`, { credentials: 'include' })
      .then(r => {
        if (r.status === 403) throw new Error('You do not have permission to view this report.');
        if (!r.ok) throw new Error('Failed to load report');
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const triggerBars = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    observe(el);
    const id = el.dataset.section || '';
    if (barsTriggered.current.has(id)) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          barsTriggered.current.add(id);
          el.querySelectorAll('.tlr-mbar-fill, .tlr-cost-bar-fill').forEach(b => {
            setTimeout(() => b.classList.add('go'), 120);
          });
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.07 });
    io.observe(el);
  }, [observe]);

  const handleDownloadPDF = useCallback(async () => {
    if (generatingPdf || !orgId) return;
    setGeneratingPdf(true);
    try {
      const response = await fetch(`/api/team/${orgId}/leader/pdf`);
      if (!response.ok) throw new Error(`PDF generation failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EQUIP360-Team-Leader-Report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  }, [generatingPdf, orgId]);

  if (loading) {
    return (
      <div className="tlr-loading">
        <div className="tlr-loading-spinner" />
        <p>Loading team leader report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tlr-loading">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9888;</div>
        <p style={{ maxWidth: 400, textAlign: 'center', lineHeight: 1.6 }}>{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { organization, team_summary, members, strengths, gaps, gap_sidebar, chemistry, pressure, actions, generated_narratives } = data;
  const healthScore = team_summary.health_score;
  const circumference = 2 * Math.PI * 112;
  const healthOffset = circumference * (1 - healthScore / 100);
  const dateStr = new Date(organization.report_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const amplifiers = chemistry.filter(p => p.tier === 'amplifier');
  const tensioners = chemistry.filter(p => p.tier === 'tensioner');
  const watchlist = chemistry.filter(p => p.tier === 'watchlist');

  return (
    <div className={`tlr-report${isPrintMode ? ' tlr-print-mode' : ''}`}>
      <div id="tlr-progress-bar" />

      <nav className="tlr-nav">
        <div className="tlr-nav-left">
          <span className="tlr-nav-logo">E.Q.U.I.P. 360</span>
          <span className="tlr-nav-badge">Team Report</span>
        </div>
        <ul className={`tlr-nav-links${mobileMenuOpen ? ' tlr-nav-links-open' : ''}`}>
          <li><a href="#tlr-picture" onClick={() => setMobileMenuOpen(false)}>Your People</a></li>
          <li><a href="#tlr-strengths" onClick={() => setMobileMenuOpen(false)}>Strengths</a></li>
          <li><a href="#tlr-gaps" onClick={() => setMobileMenuOpen(false)}>Gaps</a></li>
          <li><a href="#tlr-chemistry" onClick={() => setMobileMenuOpen(false)}>Chemistry</a></li>
          <li><a href="#tlr-pressure" onClick={() => setMobileMenuOpen(false)}>Under Pressure</a></li>
          <li><a href="#tlr-moves" onClick={() => setMobileMenuOpen(false)}>Your Moves</a></li>
        </ul>
        <span className="tlr-nav-conf">Confidential</span>
        <button className="tlr-nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* HERO */}
      <section id="tlr-hero" className="tlr-section">
        <div className="tlr-hero-bg" />
        <div className="tlr-wrap">
          <div className="tlr-hero-layout">
            <div>
              <p className={`tlr-hero-meta tlr-reveal${heroVisible ? ' visible' : ''}`}>
                Team Intelligence Report &nbsp;&middot;&nbsp; {dateStr} &nbsp;&middot;&nbsp; Assessed by {organization.leader.name}
              </p>
              <h1 className={`tlr-hero-org tlr-reveal tlr-d1${heroVisible ? ' visible' : ''}`}>
                {organization.name}
              </h1>
              <p className={`tlr-hero-sub tlr-reveal tlr-d2${heroVisible ? ' visible' : ''}`}>
                {team_summary.member_count} team members assessed &nbsp;&middot;&nbsp; Report prepared for {organization.leader.name}{organization.leader.role ? `, ${organization.leader.role}` : ''}
              </p>
              <div className={`tlr-hero-chips tlr-reveal tlr-d3${heroVisible ? ' visible' : ''}`}>
                <span className={`tlr-chip ${chipClass(tierColor(team_summary.health_score) === '#35C785' ? 'green' : tierColor(team_summary.health_score) === '#F0A030' ? 'amber' : tierColor(team_summary.health_score) === '#E05858' ? 'red' : 'gold')}`}>
                  Team EQ Average: {healthScore}%
                </span>
                <span className="tlr-chip tlr-ch-red">Critical Gap: {team_summary.critical_gap_pillar}</span>
                <span className="tlr-chip tlr-ch-green">Strength: {team_summary.top_strength_pillar}</span>
                <span className="tlr-chip tlr-ch-muted">{team_summary.watchlist_pair_count} Watchlist Pair{team_summary.watchlist_pair_count !== 1 ? 's' : ''}</span>
              </div>
              <div className={`tlr-hero-intro tlr-reveal tlr-d4${heroVisible ? ' visible' : ''}`}>
                This report tells you three things: <strong>where your team is strong</strong>, <strong>where it is exposed</strong>, and <strong>what you should do about it</strong>. Every section answers a question you are already asking as their leader.
              </div>
            </div>

            <div className={`tlr-health-ring-wrap tlr-reveal tlr-d2${heroVisible ? ' visible' : ''}`}>
              <div className="tlr-health-ring">
                <div className="tlr-health-glow" />
                <svg width="260" height="260" viewBox="0 0 260 260">
                  <circle cx="130" cy="130" r="112" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="10" />
                  <circle cx="130" cy="130" r="112" fill="none"
                    stroke="url(#tlr-hg)" strokeWidth="10"
                    strokeDasharray={circumference}
                    strokeDashoffset={isPrintMode ? healthOffset : heroVisible ? healthOffset : circumference}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(0.4,0,0.2,1) 0.3s' }}
                  />
                  <defs>
                    <linearGradient id="tlr-hg" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#4A6CF5" />
                      <stop offset="100%" stopColor="#E8C870" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="tlr-health-inner">
                  <HealthCounter target={healthScore} visible={heroVisible || isPrintMode} />
                  <span className="tlr-health-tier" style={{ color: tierColor(healthScore) }}>
                    {team_summary.health_tier.charAt(0).toUpperCase() + team_summary.health_tier.slice(1)}
                  </span>
                  <span className="tlr-health-label">Team Health Score</span>
                </div>
              </div>
              <div className="tlr-signal-row">
                <div className="tlr-signal">
                  <div className="tlr-signal-val" style={{ color: tierColor(team_summary.awareness_avg) }}>{team_summary.awareness_avg}%</div>
                  <div className="tlr-signal-name">Awareness</div>
                </div>
                <div className="tlr-signal">
                  <div className="tlr-signal-val" style={{ color: tierColor(team_summary.drive_avg) }}>{team_summary.drive_avg}%</div>
                  <div className="tlr-signal-name">Drive</div>
                </div>
                <div className="tlr-signal">
                  <div className="tlr-signal-val" style={{ color: tierColor(team_summary.culture_impact_avg) }}>{team_summary.culture_impact_avg}%</div>
                  <div className="tlr-signal-name">Culture Impact</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 1 — YOUR PEOPLE */}
      <section id="tlr-picture" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Your People</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>Who is on this team<br />and where do they stand?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>Named, honest, and specific. Each card shows that person's overall EQ health, their five emotional skill scores, and the one thing you most need to know about them as their leader.</p>

          <div className="tlr-picture-intro tlr-reveal tlr-d2" ref={observe}>
            <p>{generated_narratives.picture_intro_p1}</p>
            <p style={{ marginTop: 12 }}>{generated_narratives.picture_intro_p2}</p>
          </div>

          <div className="tlr-member-grid tlr-reveal tlr-d3" ref={triggerBars} data-section="members">
            {members.map(m => {
              const pillars = [
                { key: 'self_awareness', abbr: 'SA', val: m.eq_pillars.self_awareness },
                { key: 'self_regulation', abbr: 'SR', val: m.eq_pillars.self_regulation },
                { key: 'motivation', abbr: 'MT', val: m.eq_pillars.motivation },
                { key: 'empathy', abbr: 'EM', val: m.eq_pillars.empathy },
                { key: 'social_skill', abbr: 'SS', val: m.eq_pillars.social_skill },
              ];

              return (
                <div className="tlr-mcard" key={m.id}>
                  <div className="tlr-mcard-top">
                    <div className="tlr-mavatar" style={{ background: m.family_color }}>{m.initials}</div>
                    <div>
                      <div className="tlr-mname">{m.name}</div>
                      {m.role && <div className="tlr-mrole">{m.role}</div>}
                    </div>
                  </div>
                  <div className="tlr-mcard-scores">
                    <div className="tlr-mscore-main">
                      <span className="tlr-mscore-num" style={{ color: tierColor(m.overall_score) }}>{m.overall_score}%</span>
                      <span className="tlr-mscore-label">Overall EQ</span>
                    </div>
                    <div className="tlr-mbar-row">
                      {pillars.map(p => (
                        <div className="tlr-mbar-col" key={p.key}>
                          <div className="tlr-mbar-track">
                            <div
                              className={`tlr-mbar-fill${isPrintMode ? ' go' : ''}`}
                              style={{ background: barColor(p.val), ['--h' as string]: `${p.val}%` }}
                            />
                          </div>
                          <span className="tlr-mbar-abbr">{p.abbr}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="tlr-attention-flag" style={{ background: flagBg(m.attention_flag.color), border: flagBorder(m.attention_flag.color) }}>
                    <div className="tlr-flag-dot" style={{ background: flagDotColor(m.attention_flag.color) }} />
                    <span style={{ fontSize: 12, color: 'var(--tlr-text2)' }}>
                      <strong style={{ color: flagLabelColor(m.attention_flag.color) }}>{m.attention_flag.label}:</strong> {m.attention_flag.text}
                    </span>
                  </div>
                  <div className="tlr-mcard-footer">
                    <span className={`tlr-chip ${chipClass(m.attention_flag.color === 'red' ? 'red' : m.attention_flag.color === 'green' ? 'green' : m.attention_flag.color === 'gold' ? 'gold' : m.attention_flag.color === 'amber' ? 'amber' : 'muted')}`} style={{ fontSize: 8 }}>{m.identity_type}</span>
                    <span className="tlr-chip tlr-ch-muted" style={{ fontSize: 8 }}>{m.leadership_family.charAt(0).toUpperCase() + m.leadership_family.slice(1)} Family</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="tlr-legend-bar tlr-reveal tlr-d4" ref={observe}>
            <span><strong>SA</strong> Self-Awareness</span>
            <span><strong>SR</strong> Self-Regulation</span>
            <span><strong>MT</strong> Motivation</span>
            <span><strong>EM</strong> Empathy</span>
            <span><strong>SS</strong> Social Skill</span>
            <span style={{ marginLeft: 'auto' }}>Bars are proportional to score. Taller = stronger.</span>
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 2 — STRENGTHS */}
      <section id="tlr-strengths" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Team Strengths</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>What is this team<br />genuinely great at?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>These are not participation trophies. These are real capabilities your team has built — and the business implications of each one.</p>

          <div className="tlr-strength-grid tlr-reveal tlr-d2" ref={observe}>
            {strengths.map((s, i) => (
              <div className="tlr-scard" key={i}>
                <div className="tlr-scard-accent" style={{ background: `linear-gradient(90deg, ${s.color === 'green' ? 'var(--tlr-green)' : s.color === 'amber' ? 'var(--tlr-amber)' : 'var(--tlr-blue)'}, transparent)` }} />
                <span className="tlr-scard-icon">{s.icon}</span>
                <div className="tlr-scard-title">{s.title}</div>
                <div className="tlr-scard-score" style={{ color: s.color === 'green' ? 'var(--tlr-green)' : s.color === 'amber' ? 'var(--tlr-amber)' : '#8AA4FF' }}>
                  {s.score_display}{s.pillar === 'execution_energy' ? '' : ' avg'}
                </div>
                <p className="tlr-scard-desc">{s.description}</p>
                <div className="tlr-scard-names">
                  {s.top_contributors.map((c, j) => (
                    <span key={j} className={`tlr-chip ${chipClass(s.color)}`} style={{ fontSize: 9 }}>
                      {c.name} {c.score}%
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="tlr-so-card tlr-reveal tlr-d3" ref={observe}>
            <strong>What to do with these strengths:</strong> {generated_narratives.strengths_so_what}
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 3 — GAPS */}
      <section id="tlr-gaps" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Team Gaps</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>Where is this team<br />most exposed?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>These are not just low scores. These are the specific places where your team will underperform, create friction, or miss opportunities — unless you address them.</p>

          <div className="tlr-gap-layout tlr-reveal tlr-d2" ref={triggerBars} data-section="gaps">
            <div className="tlr-gap-main">
              {gaps.map((g, i) => (
                <div className="tlr-gcard" key={i} style={{ borderColor: gapBorderColor(g.priority) }}>
                  <div className="tlr-gcard-num" style={{ color: gapPriorityColor(g.priority) }}>
                    {String(g.number).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="tlr-gcard-label" style={{ color: gapPriorityColor(g.priority) }}>{g.priority_label}</div>
                    <div className="tlr-gcard-title">{g.title}</div>
                    <p className="tlr-gcard-desc">{g.description}</p>
                    <p className="tlr-gcard-who"><strong>{g.most_affected_text}</strong></p>
                    <div className="tlr-cost-bar-wrap">
                      <div className="tlr-cost-bar-label">
                        <span>{g.bar_label}</span>
                        <span style={{ color: gapPriorityColor(g.priority), fontWeight: 700 }}>{g.bar_display}</span>
                      </div>
                      <div className="tlr-cost-bar-track">
                        <div
                          className={`tlr-cost-bar-fill${isPrintMode ? ' go' : ''}`}
                          style={{ background: gapPriorityColor(g.priority), ['--w' as string]: `${g.bar_avg}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="tlr-gap-side">
              <div className="tlr-gside-card" style={{ borderColor: 'rgba(224,88,88,0.2)', background: 'var(--tlr-red-bg)' }}>
                <div className="tlr-gside-title" style={{ color: '#FF8888' }}>{gap_sidebar.biggest_risk.title}</div>
                <div className="tlr-gside-score" style={{ color: 'var(--tlr-red)' }}>{gap_sidebar.biggest_risk.score}</div>
                <p className="tlr-gside-desc">{gap_sidebar.biggest_risk.description}</p>
              </div>
              <div className="tlr-gside-card" style={{ borderColor: 'rgba(240,160,48,0.2)' }}>
                <div className="tlr-gside-title" style={{ color: '#FFBC58' }}>{gap_sidebar.most_urgent_person.title}</div>
                <div className="tlr-gside-score" style={{ color: 'var(--tlr-red)' }}>{gap_sidebar.most_urgent_person.score}</div>
                <p className="tlr-gside-desc">{gap_sidebar.most_urgent_person.description}</p>
              </div>
              <div className="tlr-gside-card" style={{ borderColor: 'rgba(53,199,133,0.2)' }}>
                <div className="tlr-gside-title" style={{ color: '#6DDEA8' }}>{gap_sidebar.fastest_win.title}</div>
                <div className="tlr-gside-score" style={{ color: 'var(--tlr-green)' }}>{gap_sidebar.fastest_win.score}</div>
                <p className="tlr-gside-desc">{gap_sidebar.fastest_win.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 4 — CHEMISTRY */}
      <section id="tlr-chemistry" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Team Chemistry</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>Who works well together —<br />and who needs your attention?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>Based on how each person's emotional patterns interact with everyone else's. Not about whether they like each other — about what happens between them when the pressure is on.</p>

          <div className="tlr-chem-intro tlr-reveal tlr-d2" ref={observe}>
            <strong>How to read this:</strong> Every pairing has a pattern. The goal is not to separate people who are on the Watchlist — it is to lead those relationships intentionally. The leaders who understand these dynamics before a crisis are the ones who never have to manage the crisis.
          </div>

          <div className="tlr-chem-cols tlr-reveal tlr-d3" ref={observe}>
            <div className="tlr-chem-col tlr-col-green">
              <div className="tlr-chem-col-head">
                <span className="tlr-chem-col-icon">⚡</span>
                <div className="tlr-chem-col-title">Amplifiers</div>
                <div className="tlr-chem-col-sub">These pairs bring out the best in each other. They cover each other's blind spots naturally.</div>
              </div>
              <div className="tlr-chem-pairs">
                {amplifiers.length === 0 && <p style={{ fontSize: 12, color: 'var(--tlr-text3)', padding: 8 }}>No amplifier pairs identified.</p>}
                {amplifiers.map((p, i) => (
                  <ChemPairCard key={i} pair={p} />
                ))}
              </div>
            </div>

            <div className="tlr-chem-col tlr-col-amber">
              <div className="tlr-chem-col-head">
                <span className="tlr-chem-col-icon">🔄</span>
                <div className="tlr-chem-col-title">Productive Tension</div>
                <div className="tlr-chem-col-sub">Natural friction that is useful when named. Left unnamed, it becomes unnecessary conflict.</div>
              </div>
              <div className="tlr-chem-pairs">
                {tensioners.length === 0 && <p style={{ fontSize: 12, color: 'var(--tlr-text3)', padding: 8 }}>No productive tension pairs identified.</p>}
                {tensioners.map((p, i) => (
                  <ChemPairCard key={i} pair={p} />
                ))}
              </div>
            </div>

            <div className="tlr-chem-col tlr-col-red">
              <div className="tlr-chem-col-head">
                <span className="tlr-chem-col-icon">⚠️</span>
                <div className="tlr-chem-col-title">Watchlist</div>
                <div className="tlr-chem-col-sub">Their stress patterns collide. You need to lead this proactively.</div>
              </div>
              <div className="tlr-chem-pairs">
                {watchlist.length === 0 && <p style={{ fontSize: 12, color: 'var(--tlr-text3)', padding: 8 }}>No watchlist pairs identified.</p>}
                {watchlist.map((p, i) => (
                  <ChemPairCard key={i} pair={p} showAction />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 5 — UNDER PRESSURE */}
      <section id="tlr-pressure" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Under Pressure</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>What does this team actually do<br />when it gets hard?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>Every person on this team has a default stress pattern. Named, it loses its power. Here is what each person does — and the collective result.</p>

          <div className="tlr-pressure-card tlr-reveal tlr-d2" ref={observe}>
            <div className="tlr-pressure-headline">Here is what happens when this team hits a genuine crisis:</div>
            <div className="tlr-pressure-people">
              {pressure.per_person.map(pp => (
                <div className="tlr-pp-row" key={pp.member_id}>
                  <div className="tlr-pp-who">
                    <div className="tlr-pp-av" style={{ background: pp.family_color }}>{pp.initials}</div>
                    <div className="tlr-pp-name">{pp.name}</div>
                  </div>
                  <div className="tlr-pp-what">{renderBoldText(pp.description)}</div>
                </div>
              ))}
            </div>

            <div className="tlr-pressure-result">
              <strong>The result:</strong> {generated_narratives.collective_pressure_pattern}
            </div>
          </div>

          <div className="tlr-pressure-antidote tlr-reveal tlr-d3" ref={observe}>
            <h4>What You Do About It</h4>
            <p>{generated_narratives.pressure_leader_protocol}</p>
          </div>
        </div>
      </section>

      <div className="tlr-divider" />

      {/* SECTION 6 — YOUR MOVES */}
      <section id="tlr-moves" className="tlr-section">
        <div className="tlr-wrap">
          <div className="tlr-eyebrow tlr-reveal" ref={observe}>Your Moves</div>
          <h2 className="tlr-section-q tlr-reveal tlr-d1" ref={observe}>What do you do<br />with all of this?</h2>
          <p className="tlr-section-sub tlr-reveal tlr-d2" ref={observe}>Six specific, prioritized actions. Each one has a name on it and a reason behind it.</p>

          <div className="tlr-moves-list tlr-reveal tlr-d2" ref={observe}>
            {actions.map((a, i) => (
              <div className="tlr-move" key={i}>
                <div className="tlr-move-num-col">
                  <div className="tlr-move-num" style={{ color: `var(--tlr-${a.urgency_color === 'gold' ? 'gold' : a.urgency_color})` }}>
                    {String(a.number).padStart(2, '0')}
                  </div>
                  <div className="tlr-move-urgency" style={{ color: `var(--tlr-${a.urgency_color === 'gold' ? 'gold' : a.urgency_color})` }}>
                    {a.urgency}
                  </div>
                </div>
                <div className="tlr-move-body">
                  <div className="tlr-move-title">{a.title}</div>
                  <p className="tlr-move-desc">{a.description}</p>
                  <div className="tlr-move-details">
                    {a.who.map((w, j) => (
                      <div className="tlr-move-who" key={j}>
                        <div className="tlr-move-av-sm" style={{ background: w.color }}>{w.initials}</div>
                        {w.name}
                      </div>
                    ))}
                    <span className={`tlr-chip ${chipClass(a.tied_to_chip_color)}`} style={{ fontSize: 8 }}>
                      {a.tied_to_label}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="tlr-cta-block tlr-reveal tlr-d3" ref={observe}>
            <div className="tlr-cta-text">
              <h3>Ready to build this team intentionally?</h3>
              <p>The data is clear. The actions are specific. The next step is yours. Every leader who has gone through the E.Q.U.I.P. 360 Accelerator says the same thing: &quot;I wish I had this sooner.&quot;</p>
            </div>
            <div className="tlr-cta-btns">
              <button className="tlr-btn-p" onClick={handleDownloadPDF} disabled={generatingPdf}>
                {generatingPdf ? 'Generating PDF...' : 'Download PDF Report'}
              </button>
              <a href={`/team/${orgId}/overview`} className="tlr-btn-s">
                View Team Overview
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="tlr-footer">
        <div className="tlr-wrap">
          <div className="tlr-footer-in">
            <div className="tlr-footer-brand">
              <strong>E.Q.U.I.P. 360</strong>
              Team Intelligence Report · Confidential
            </div>
            <div style={{ fontSize: 11, color: 'var(--tlr-text3)' }}>
              &copy; {new Date().getFullYear()} E.Q.U.I.P. 360. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ChemPairCard({ pair, showAction }: { pair: ChemistryPair; showAction?: boolean }) {
  const tierChip = pair.tier === 'amplifier' ? 'green' : pair.tier === 'tensioner' ? 'amber' : 'red';
  return (
    <div className="tlr-cpair">
      <div className="tlr-cpair-who">
        <div className="tlr-cpair-avs">
          <div className="tlr-cpair-av" style={{ background: pair.member_a.family_color }}>{pair.member_a.initials}</div>
          <div className="tlr-cpair-av" style={{ background: pair.member_b.family_color }}>{pair.member_b.initials}</div>
        </div>
        <div className="tlr-cpair-names">{pair.member_a.name} + {pair.member_b.name}</div>
      </div>
      <p className="tlr-cpair-why">{pair.description}</p>
      <div className="tlr-cpair-tag">
        <span className={`tlr-chip ${chipClass(tierChip)}`} style={{ fontSize: 8 }}>{pair.pair_label}</span>
      </div>
      {showAction && pair.leader_action && (
        <div className="tlr-cpair-action">Leader action: {pair.leader_action}</div>
      )}
    </div>
  );
}
